import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Label } from './label';
import { ScrollArea } from './scroll-area';
import { Loader2, Download, CheckCircle } from 'lucide-react';
import { logger } from '../../utils/logger';

/**
 * Reusable Download Wizard Component
 * Shows a dialog with column selection for CSV export
 * 
 * Props:
 * - open: boolean - Whether dialog is open
 * - onOpenChange: (open: boolean) => void
 * - title: string - Dialog title
 * - description: string - Dialog description
 * - columns: Array<{key: string, label: string}> - Available columns
 * - onDownload: (selectedColumns: string[]) => Promise<void> - Download handler
 * - defaultSelected: string[] - Default selected columns (optional)
 */
export function DownloadWizard({ 
  open, 
  onOpenChange, 
  title = "Download Data",
  description = "Select the columns you want to include in the export.",
  columns = [],
  onDownload,
  defaultSelected = []
}) {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize with default or all columns
      if (defaultSelected.length > 0) {
        setSelectedColumns(defaultSelected);
      } else {
        setSelectedColumns(columns.map(c => c.key));
      }
      setSuccess(false);
    }
  }, [open, columns, defaultSelected]);

  const handleToggleColumn = (columnKey) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(k => k !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns(columns.map(c => c.key));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleDownload = async () => {
    if (selectedColumns.length === 0) return;
    
    setDownloading(true);
    try {
      await onDownload(selectedColumns);
      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
      }, 1500);
    } catch (error) {
      logger.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-gold" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-emerald-700">Download Started!</p>
            <p className="text-sm text-slate-500 mt-1">Your file is being downloaded...</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-500">
                {selectedColumns.length} of {columns.length} selected
              </span>
              <div className="flex gap-2">
                <Button variant="link" size="sm" onClick={handleSelectAll} className="h-auto p-0">
                  Select All
                </Button>
                <span className="text-slate-300">|</span>
                <Button variant="link" size="sm" onClick={handleDeselectAll} className="h-auto p-0">
                  Deselect All
                </Button>
              </div>
            </div>

            <ScrollArea className="h-64 border rounded-lg p-3">
              <div className="space-y-2">
                {columns.map((column) => (
                  <label
                    key={column.key}
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-md cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedColumns.includes(column.key)}
                      onCheckedChange={() => handleToggleColumn(column.key)}
                    />
                    <span className="text-sm">{column.label}</span>
                  </label>
                ))}
              </div>
            </ScrollArea>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDownload}
                disabled={selectedColumns.length === 0 || downloading}
                className="bg-gold hover:bg-gold/90 text-slate-900"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DownloadWizard;
